/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react'
import { Form, Input, Typography, Button, Checkbox, notification } from 'antd'
import { useTranslation } from 'react-i18next' 
import { sendEmailFunc } from '../App'
import { subscribeToNewsletter } from '../utils/newsletter' 
import { 
  getConfirmationEmailTemplate, 
  extractFirstName, 
  EmailTemplateData 
} from '../templates/confirmationEmailTemplate'
const { TextArea } = Input
const { Link } = Typography


function ReviewForm() {
  const { t, i18n } = useTranslation('landing') 
  const [form] = Form.useForm()
  const [ isSubmitting, setIsSubmitting ] = useState(false)

  const onFinish = async (values: any) => {
    setIsSubmitting(true)
    console.log('Received values of form: ', values)

    try {
      // Prepare admin notification email
      const messageBody = Object.entries(values)
        .map(([ key, value ]) => {
          if (key === 'consent' || key === 'marketingConsent') { 
            return `${key}: ${value ? 'Agreed' : 'Not Agreed'}`
          }
          return `${key}: ${value}`
        })
        .join('<br />')

      const adminEmailPayload = {
        email: 'support@colozoo.de',
        subject: 'Colozoo - New Free Gift Claim Submitted!', 
        message: messageBody
      }

      // Send admin notification email
      await sendEmailFunc(JSON.stringify(adminEmailPayload))      // Prepare customer confirmation email
      const customerFirstName = values.name ? extractFirstName(values.name) : extractFirstName(values.email)
      const fullAddress = `${values.name || ''}\n${values.street || ''}\n${values.city || ''} ${values.postalCode || ''}\n${values.country || ''}`.trim()
      
      // Use current page language directly
      const currentLanguage = i18n.language || 'en'
      console.log('Current i18n language:', currentLanguage)      // Map i18n language to email template language
      let emailLanguage = currentLanguage
      // Handle both English variants directly from language selector
      if (currentLanguage === 'en') {
        emailLanguage = 'en-GB' // Fallback if somehow still 'en'
      }
      // For en-GB, en-US, de, fr, it, es - use directly
      
      console.log('Final email language:', emailLanguage)
      
      const emailTemplateData: EmailTemplateData = {
        customerFirstName,
        orderNumber: values.orderNumber || '',
        reviewLink: values.reviewLink || '',
        email: values.email || '',
        fullAddress,
        productNames: values.desiredProduct || ''
      }

      const confirmationEmail = getConfirmationEmailTemplate(emailTemplateData, emailLanguage)
      
      const customerEmailPayload = {
        email: values.email,
        subject: confirmationEmail.subject,
        message: confirmationEmail.body
      }

      // Send customer confirmation email
      await sendEmailFunc(JSON.stringify(customerEmailPayload))

      if (values.marketingConsent && values.email) {
        try {
          await subscribeToNewsletter({
            email: values.email,
            language: i18n.language || 'en-GB',
            source: 'landing-review-form',
          })
        } catch (error) {
          console.error('Could not add user to newsletter:', error)
        }
      }

      notification.success({
        message: t('form.submissionSuccessTitle') || 'Form Submitted!',
        description: t('form.submissionSuccessMessage') || 'Thank you! You\'ll get a confirmation message right after submitting. If you don\'t hear from us within 5 days, feel free to reach out to support@colozoo.de!',
        duration: 9,
      })
      form.resetFields()

    } catch (error) {
      console.error('Failed to send email:', error)
      notification.error({
        message: t('form.submissionErrorTitle') || 'Submission Error',
        description: t('form.submissionErrorMessage') || 'There was a problem submitting your claim. Please try again or contact support.',
        duration: 6,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo)
    notification.error({
      message: t('form.submissionFailedTitle') || 'Submission Failed',
      description: t('form.submissionFailedMessage') || 'Please check the form fields for errors.',
    })
  }

  return (
    <Form
      form={form}
      name="review_submission"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      requiredMark="optional" 
      style={{ maxWidth: '600px', margin: '20px auto' }}
    >
      {/* Link to Review */}
      <Form.Item
        label={<>{t('form.reviewLinkLabel') || ''}</>}
        name="reviewLink"
        rules={[
          { required: true, message: t('form.reviewLinkRequiredMsg') || '' },
          { type: 'url', warningOnly: true, message: t('form.reviewLinkValidUrlMsg') || '' }
        ]}
      >
        <Input placeholder={t('form.reviewLinkPlaceholder') || ''} />
      </Form.Item>

      {/* Order Number */}
      <Form.Item
        label={<>{t('form.orderNumberLabel') || ''}</>}
        name="orderNumber"
        rules={[{ required: true, message: t('form.orderNumberRequiredMsg') || '' }]}
      >
        <Input placeholder={t('form.orderNumberPlaceholder') || ''} />
      </Form.Item>

      {/* Email Address */}
      <Form.Item
        label={<>{t('form.emailLabel') || ''}</>}
        name="email"
        rules={[
          { required: true, message: t('form.emailRequiredMsg') || '' },
          { type: 'email', message: t('form.emailValidMsg') || '' }
        ]}
      >
        <Input placeholder={t('form.emailPlaceholder') || ''} />
      </Form.Item>      {/* Shipping Address */}
      <Form.Item label={t('form.shippingAddressLabel') || ''}>
        <Form.Item
          name="name"
          style={{ marginBottom: 10 }}
        >
          <Input placeholder={t('form.namePlaceholder') || 'Full Name'} />
        </Form.Item>
        <Form.Item
          name="street"
          style={{ marginBottom: 10 }}
        >
          <Input placeholder={t('form.streetPlaceholder') || ''} />
        </Form.Item>
        <Form.Item
          name="city"
          style={{ marginBottom: 10 }}
        >
          <Input placeholder={t('form.cityPlaceholder') || ''} />
        </Form.Item>
        <Form.Item
          name="postalCode"
          style={{ marginBottom: 10 }}
        >
          <Input placeholder={t('form.postalCodePlaceholder') || ''} />
        </Form.Item>
        <Form.Item
          name="country"
          style={{ marginBottom: 0 }}
        >
          <Input placeholder={t('form.countryPlaceholder') || ''} />
        </Form.Item>
      </Form.Item>

      {/* Desired Product */}
      <Form.Item
        label={
          <span>
            {t('form.desiredProductLabel') || ''}{' '}
            {/* @ts-ignore */}
            <Link href={t('links.storeLink')} target="_blank" rel="noopener noreferrer">
              {t('form.desiredProductLinkText') || ''}
            </Link>
          </span>
        }
        name="desiredProduct"
        rules={[{ required: true, message: t('form.desiredProductRequiredMsg') || '' }]}
      >
        <Input.TextArea rows={3} placeholder={t('form.desiredProductPlaceholder') || ''} />
      </Form.Item>

      {/* Consent Checkbox */}
      <Form.Item
        name="consent"
        valuePropName="checked"
        rules={[
          { validator:(_, value) => value ? Promise.resolve() : Promise.reject(new Error(t('form.consentRequiredMsg') || '')) },
        ]}
      >
        <Checkbox>
          {t('form.consentCheckboxLabel') || ''}
        </Checkbox>
      </Form.Item>

      {/* Marketing Consent Checkbox */}
      <Form.Item
        name="marketingConsent"
        valuePropName="checked"
      >
        <Checkbox>
          {t('form.marketingConsentCheckboxLabel') || ''}
        </Checkbox>
      </Form.Item>

      {/* Submit Button */}
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isSubmitting}>
          {isSubmitting ? (t('form.submittingButtonText') || '') : (t('form.submitButtonText') || '')}
        </Button>
      </Form.Item>
    </Form>
  )
}

export default ReviewForm